import { Market, SportMatchModel } from '@nf/types/odds';
import { SpeacialMainBetTypes } from '../specail-main-bet-types'
import { getSpecialSportSetting } from '@/services/settings.service';
import { useGetBetTypeName } from '@/hooks';
import { CloseBigIcon, DropdownIcon } from '@/components/icons/svg-icons';
import { BetTypeContent } from '@/features/single-match/bet-types-section/components/bet-type-content';
import { Box, Maybe, MaybeMatch, matchEquals } from '@nf/utils-common/fpw/monadic';
import { itemsEmptyCheck } from '@nf/utils-common/fpw/utils';
import { cx } from 'class-variance-authority';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { matchSpecialStyles } from '../match-special.style';
import { betTypeService } from '@/services/bettype.service';
import { filterBetTypesFromMarkes } from '@/utils/odds-utils';
import { useTranslations } from 'next-intl';
import { UserInfo } from '@nf/types/account';
import { SpecialSportSetting } from '@/settings/special-sport-setting';
import { useOddsTableMarsContext } from '@/features/odds-table-mars/context';
import { MatchOddsLoadingWrapper } from '@/features/odds-page/match-odds-loading-wrapper';
import { MarketLabel } from '@/types';

interface MatchSpecialBetTypeProps {
    match: SportMatchModel
    markets: Market[];
    isLoading: boolean;
    isShowMatch: boolean;
    user?: UserInfo | null,
    homeTeamName: string;
    awayTeamName: string;
}
export const MatchSpecialBetType = ({
	match,
	markets,
	isLoading,
	isShowMatch,
	homeTeamName,
	awayTeamName
}: MatchSpecialBetTypeProps) => {
	const { IsLive } = match;
	const { sport } = useOddsTableMarsContext();
	const [showMoreLines, setShowMoreLines] = useState(false);
	const [showSecondMarket, setShowSecondMarket] = useState(isShowMatch);
	const [isArranging, setIsArranging] = useState<boolean>(true);
	const t = useTranslations('General');
	const [mainBetTypeMarket, setMainBetTypeMarket] = useState<Map<number, Market | undefined>>(new Map());
	const [otherMainBetTypeMarket, setOtherMainBetTypeMarket] = useState<Map<string | number, Market | undefined>>(new Map());
	const [moreBetTypeMarkets, setMoreBetTypeMarkets] = useState<Map<string | number, Market | undefined>[]>([]);
	const [secondMarket, setSecondMarket] = useState<Market>();
	const toggleMoreLines = () => {
		setShowMoreLines(!showMoreLines);
	};
	const baseSpecialSportSetting = getSpecialSportSetting(sport);
	const getBetTypeName = useGetBetTypeName();


	useEffect(() => {
		itemsEmptyCheck(markets)
			.chain(() => Maybe(baseSpecialSportSetting))
			.chain(() => MaybeMatch(!isLoading))
			.fold(
				() => {},
				() => {
					const specialSportSetting: SpecialSportSetting = matchEquals(sport)
						.on(50, () => getCricketSportSetting(markets, baseSpecialSportSetting, IsLive))
						.otherwise(() => baseSpecialSportSetting);
					const mainGroupMap = betTypeService.getMainGroupMap(markets, specialSportSetting.mainGroupBetTypes);
					setMainBetTypeMarket(mainGroupMap);
					const otherMainGroup = specialSportSetting.mainOtherGroup ? betTypeService.getOtherMainGroupMap(markets, specialSportSetting.mainOtherGroup) : new Map();
					setOtherMainBetTypeMarket(otherMainGroup);
					const mainMarketIds = [...mainGroupMap?.values()].map(market => market?.MarketId ?? 0);
					const otherMainMarketIds =  [...otherMainGroup?.values()].map(market => market?.MarketId ?? 0);
					const excludeMarket = new Set([...mainMarketIds, ...otherMainMarketIds])
					specialSportSetting.moreGroup && setMoreBetTypeMarkets(betTypeService.getMoreGroup(markets.filter(market => !excludeMarket.has(market.MarketId)), specialSportSetting.moreGroup))
					if (specialSportSetting.baseBetType) {
						const market = markets.find(market => market.BetTypeId === specialSportSetting.baseBetType && !excludeMarket.has(market.MarketId))
						market && setSecondMarket(market);
					}
					isArranging && setIsArranging(false)
				}
			)

	}, [isLoading, markets, IsLive])
	
	return (
		<MatchOddsLoadingWrapper isLoading={isArranging} >
			<SpeacialMainBetTypes 
				mainBetTypeMarketsMap={mainBetTypeMarket}
				match={match}
				homeTeamName={homeTeamName}
				awayTeamName={awayTeamName}
			/>

			<SpeacialMainBetTypes 
				mainBetTypeMarketsMap={otherMainBetTypeMarket}
				match={match}
				homeTeamName={homeTeamName}
				awayTeamName={awayTeamName}
			/>
			{
				moreBetTypeMarkets.length > 0 && (
					<motion.div
						className={matchSpecialStyles.moreline}
						initial="closed"
						animate={showMoreLines ? 'open' : 'closed'}
						variants={{
							open: { height: 'auto', padding: '6px' },
							closed: { height: '39px', padding: 0 },
						}}
						data-open={showMoreLines}
					>
						<div
							className={cx('btn-moreline', matchSpecialStyles.morelineOpenButton)}
							onClick={toggleMoreLines}
						>
							{t('lbl_MoreAsianLines')}
						</div>
						{
							moreBetTypeMarkets.map((moreBetTypeMap, index) => (
								<SpeacialMainBetTypes
									key={`${[...moreBetTypeMap.keys()][0]}_${index}`}
									mainBetTypeMarketsMap={moreBetTypeMap}
									match={match}
									homeTeamName={homeTeamName}
									awayTeamName={awayTeamName}
								/>
							))
						}
						<div
							className={matchSpecialStyles.morelineCloseButton}
							onClick={toggleMoreLines}
						>
							{ t('lbl_btnClose') }
							<CloseBigIcon />
						</div>
					</motion.div> 
				)
			}
			{
				secondMarket && (
					<div className={cx('bettype', matchSpecialStyles.bettype)} data-open={showSecondMarket}>
						<div className="bettype__header" onClick={() => setShowSecondMarket(!showSecondMarket)}>
							<div className="bettype__header-text">{getBetTypeName(secondMarket.BetTypeId, sport, secondMarket)}</div>
							<div className="bettype__header-icons">
								<DropdownIcon />
							</div>
						</div>
						<BetTypeContent matchModel={match} betTypeArray={[secondMarket]} />
					</div>
				)
			}
		</MatchOddsLoadingWrapper>
	)
}



const getCricketSportSetting = (markets: Market[], defaultSetting: SpecialSportSetting, isLive: boolean) => {
	if (!isLive) return defaultSetting;
	const cricketSetting = Object.assign({}, defaultSetting);
	markets.forEach(market => {
		switch (market.BetTypeId) {
			case 5:
				cricketSetting.mainGroupBetTypes = [9609, 9412, 5];
				break;
			case 501:
				cricketSetting.mainGroupBetTypes = [501, 9607, 9410];
				break;
			case 9607:
			case 9410:
				cricketSetting.mainGroupBetTypes = [501, 9607, 9410];
				cricketSetting.moreGroup = [[9607, 9410], [9608, 9411]];
				break;
			case 9608:
			case 9411:
				cricketSetting.mainGroupBetTypes = [501, 9608, 9411];
				cricketSetting.moreGroup = [[9608, 9411]];
				break;
			case 9609:
			case 9412:
				cricketSetting.mainGroupBetTypes = [9609, 9412, 5];
				cricketSetting.moreGroup = [[9609, 9412], [9610, 9413]];
				break;
			case 9610:
			case 9413:
				cricketSetting.mainGroupBetTypes = [9610, 9413, 5];
				cricketSetting.moreGroup = [[9610, 9413]];
				break;
			case 9611:
			case 9414:
				cricketSetting.mainGroupBetTypes = [9611, 9414, 5];
				cricketSetting.moreGroup =[[9611, 9414], [9612, 9415]];
				break;
			case 9612:
			case 9415:
				cricketSetting.mainGroupBetTypes = [9612, 9415, 5];
				cricketSetting.moreGroup =[[9612, 9415]];
				break;
		}
	})
	return cricketSetting;
}